import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AtSign, Users, Loader2, Delete, ArrowLeft, ShieldCheck, Gamepad2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useKidAuth } from "@/context/KidAuthContext";
import { AddToHomeScreenFooterLink } from "@/components/AddToHomeScreen";

const PIN_LEN = 4;

export default function KidZone() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { kid, refresh } = useKidAuth();
  const [searchParams] = useSearchParams();
  const preselectKidId = searchParams.get("kid_id");

  // Default tab logic:
  //   * If a parent is signed in on this device -> "family" avatar picker
  //     (the sweet spot — kid just taps their face)
  //   * Otherwise -> "handle" tab so the kid isn't staring at an empty
  //     avatar grid on a fresh device.
  // The initial `user` value is `null` while AuthContext hydrates, so we
  // pick "handle" as the safe default and let the effect below switch to
  // "family" if the parent session materialises.
  const [mode, setMode] = useState(
    preselectKidId ? "family" : (user && user !== false ? "family" : "handle")
  );
  const [handle, setHandle] = useState("");
  const [selectedKidId, setSelectedKidId] = useState(preselectKidId || "");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Once the kid touches any input, we lock the tab in so a late-arriving
  // parent-session hydration doesn't yank them off the tab they've started
  // filling out.
  const [tabLocked, setTabLocked] = useState(false);

  // Family roster — auto-populated when the parent is already signed in
  // on the same browser (common case: dad browses in and hands the device
  // to his kid). Uses the public household endpoint so it also works
  // without a parent session as long as the user pastes a parent id.
  const [roster, setRoster] = useState([]);

  useEffect(() => {
    // Already signed in as a kid? Send them to their workspace.
    if (kid && kid.kid_id) nav("/kidzone/workspace", { replace: true });
  }, [kid, nav]);

  // Late-arriving parent session? Switch to family tab so they get the
  // faster avatar picker — unless the kid already started typing.
  useEffect(() => {
    if (tabLocked) return;
    if (user && user !== false && user.id && mode === "handle" && !handle) {
      setMode("family");
    }
  }, [user, tabLocked, mode, handle]);

  const switchMode = (next) => {
    setTabLocked(true);
    setMode(next);
    if (next === "family") { setHandle(""); }
    else { setSelectedKidId(""); }
    setPin(""); setErr("");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Load household roster when a parent is signed in in this tab. This is
    // the sweet spot for family devices — the kid just taps their avatar.
    if (!user || user === false || !user.id) { setRoster([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/kids`);
        setRoster((data || []).map((k) => ({
          id: k.id, name: k.kid_alias || k.name, emoji: k.avatar_emoji, age: k.age,
        })));
      } catch (_e) { setRoster([]); }
    })();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = pin.length === PIN_LEN && (
    (mode === "handle" && handle.trim().length >= 2) ||
    (mode === "family" && selectedKidId)
  );

  const handleDigit = (d) => {
    setErr("");
    if (pin.length >= PIN_LEN) return;
    setPin(pin + String(d));
  };
  const handleBack = () => setPin(pin.slice(0, -1));

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true); setErr("");
    try {
      const body = { pin };
      if (mode === "handle") body.handle = handle.trim().replace(/^@/, "");
      else body.kid_id = selectedKidId;
      await api.post("/auth/kid-login", body);
      await refresh();
      nav("/kidzone/workspace", { replace: true });
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't sign in. Please try again.");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  // Auto-submit when PIN is fully entered
  useEffect(() => {
    if (pin.length === PIN_LEN && canSubmit && !busy) {
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-brandPink/10 via-white to-brandTeal/10" data-testid="kidzone-page">
      <Navbar />
      <section className="max-w-lg mx-auto px-6 lg:px-8 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="kidzone-back">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="text-center mt-8">
          <div className="inline-flex items-center gap-2 pill bg-brandPink/15 text-brandPink" data-testid="kidzone-eyebrow">
            <Gamepad2 className="w-3.5 h-3.5" /> Kid Zone
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-brandSlate mt-3" data-testid="kidzone-title">
            Welcome back!
          </h1>
          <p className="text-slate-600 mt-3 max-w-md mx-auto">
            {mode === "family"
              ? "Tap your avatar and pop in your 4-digit PIN."
              : "Type your Kid Zone handle and PIN to log in."}
          </p>
        </div>

        {/* Mode tabs */}
        <div className="mt-8 grid grid-cols-2 gap-2 bg-white rounded-2xl border border-slate-200 p-1 shadow-sm" data-testid="kidzone-mode-tabs">
          <button
            type="button"
            onClick={() => switchMode("family")}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition ${mode === "family" ? "bg-brandPink/15 text-brandPink" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="kidzone-tab-family"
          >
            <Users className="w-4 h-4" /> My family
          </button>
          <button
            type="button"
            onClick={() => switchMode("handle")}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition ${mode === "handle" ? "bg-brandPink/15 text-brandPink" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="kidzone-tab-handle"
          >
            <AtSign className="w-4 h-4" /> Handle
          </button>
        </div>

        <div className="card-base p-6 mt-4">
          {mode === "family" ? (
            roster.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6" data-testid="kidzone-family-empty">
                No family profiles loaded yet. Ask a parent to sign in on this device first — or switch to <strong>Handle</strong> above.
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3" data-testid="kidzone-family-grid">
                {roster.map((k) => (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => { setSelectedKidId(k.id); setPin(""); setErr(""); }}
                    className={`p-3 rounded-2xl border-2 text-center transition ${selectedKidId === k.id ? "border-brandPink bg-brandPink/10" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`kidzone-family-pick-${k.id}`}
                  >
                    <div className="text-3xl">{k.emoji || "🧒"}</div>
                    <div className="mt-1 text-sm font-bold text-slate-800 truncate">{k.name}</div>
                    {typeof k.age === "number" && <div className="text-[10px] text-slate-500">Age {k.age}</div>}
                  </button>
                ))}
              </div>
            )
          ) : (
            <div>
              <label htmlFor="kidzone-handle" className="text-xs font-bold uppercase tracking-wider text-slate-500">Your handle</label>
              <div className="relative mt-1">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  id="kidzone-handle"
                  type="text"
                  value={handle}
                  onChange={(e) => { setHandle(e.target.value.replace(/^@/, "").replace(/[^A-Za-z0-9._-]/g, "")); setErr(""); }}
                  placeholder="e.g. lily-snowdrop"
                  autoComplete="off"
                  className="w-full pl-9 pr-3 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brandPink"
                  data-testid="kidzone-handle-input"
                  maxLength={64}
                />
              </div>
            </div>
          )}

          {/* PIN row */}
          <div className="mt-6">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 text-center">4-digit PIN</div>
            <div className="mt-3 flex justify-center gap-3" data-testid="kidzone-pin-dots">
              {Array.from({ length: PIN_LEN }).map((_, i) => (
                <div
                  key={i}
                  className={`w-12 h-14 rounded-2xl border-2 flex items-center justify-center text-2xl font-mono font-bold transition ${pin.length > i ? "border-brandPink bg-brandPink/10 text-brandPink" : "border-slate-200 bg-white text-slate-300"}`}
                  data-testid={`kidzone-pin-dot-${i}`}
                >
                  {pin.length > i ? "•" : ""}
                </div>
              ))}
            </div>
            {err && (
              <div className="mt-3 text-center text-sm text-rose-700 font-semibold" data-testid="kidzone-error">
                {err}
              </div>
            )}

            {/* Keypad */}
            <div className="mt-6 grid grid-cols-3 gap-2 max-w-xs mx-auto" data-testid="kidzone-keypad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleDigit(n)}
                  disabled={busy || pin.length >= PIN_LEN}
                  className="py-4 rounded-2xl bg-white border border-slate-200 text-2xl font-display font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40 transition"
                  data-testid={`kidzone-key-${n}`}
                >
                  {n}
                </button>
              ))}
              <div />
              <button
                type="button"
                onClick={() => handleDigit(0)}
                disabled={busy || pin.length >= PIN_LEN}
                className="py-4 rounded-2xl bg-white border border-slate-200 text-2xl font-display font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40 transition"
                data-testid="kidzone-key-0"
              >
                0
              </button>
              <button
                type="button"
                onClick={handleBack}
                disabled={busy || pin.length === 0}
                className="py-4 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center transition"
                aria-label="Delete last digit"
                data-testid="kidzone-key-back"
              >
                <Delete className="w-5 h-5" />
              </button>
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit || busy}
              className="w-full btn-primary mt-6 disabled:opacity-50"
              data-testid="kidzone-submit-btn"
            >
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>) : "Enter Kid Zone"}
            </button>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-slate-500 flex items-center justify-center gap-1">
          <ShieldCheck className="w-3 h-3 text-emerald-500" />
          Forgot your PIN? Ask a parent to reset it from their dashboard.
        </div>

        <div className="mt-4 text-center" data-testid="kidzone-a2hs-footer">
          <AddToHomeScreenFooterLink />
        </div>
      </section>
      <Footer />
    </div>
  );
}
